import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    InputAdornment,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
    Typography,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import AdminAPI from 'config/api/AdminAPI'
import Loader from 'components/general/Loader'
import AddProviderModal from 'components/admin/AddProviderModal'
import ProviderModal from 'components/admin/ProviderModal'
import { onError } from 'config/lib/errorLib'
import { productTypes } from 'config/constants/Constants'
import {
    checkPrimaryPersonCompletion,
    checkCompanyProfileCompletion,
    checkRecommendationCardCompletion,
    checkAboutCompanyCompletion,
    checkTechnologyCompletion,
    checkProjectsCompletion,
} from 'config/helpers/Helpers'

// ---------------------------------------------------------------------------
// Profile progress helpers
// ---------------------------------------------------------------------------

const PROFILE_CHECKERS = [
    checkPrimaryPersonCompletion,
    checkCompanyProfileCompletion,
    checkRecommendationCardCompletion,
    checkAboutCompanyCompletion,
    checkTechnologyCompletion,
    checkProjectsCompletion,
]

function getProfileProgress(provider) {
    const total = PROFILE_CHECKERS.length
    const completed = PROFILE_CHECKERS.filter(fn => fn(provider) === 100).length
    const pct = Math.round((completed / total) * 100)
    return { completed, total, pct }
}

// ---------------------------------------------------------------------------
// Onboarding stage (6 steps, ascending = most stuck first)
// ---------------------------------------------------------------------------

function getOnboardingStage(provider) {
    const { completed, total } = getProfileProgress(provider)
    if (completed < total) return 1

    if (provider.profileStatus !== 'Published') return 2

    const pp = provider.primaryPerson || {}
    if (!pp.firstName || !pp.email) return 3

    const maxLeads = provider.planDetails?.maxLeads
    if (!maxLeads || maxLeads <= 0) return 4

    if (!provider.deliveryDetails?.deliveryMethod) return 5

    return 6
}

// ---------------------------------------------------------------------------
// Sort value extractor
// ---------------------------------------------------------------------------

function getSortValue(provider, columnId) {
    switch (columnId) {
        case 'name':           return (provider.name || '').toLowerCase()
        case 'profilePct':     return getProfileProgress(provider).pct
        case 'published':      return provider.profileStatus === 'Published' ? 1 : 0
        case 'adminAdded': {
            const pp = provider.primaryPerson || {}
            return pp.firstName && pp.email ? 1 : 0
        }
        case 'weeklyCap':      return provider.planDetails?.maxLeads ?? -1
        case 'deliveryMethod': return (provider.deliveryDetails?.deliveryMethod || '').toLowerCase()
        case 'activeDelivery': return provider.planDetails?.pauseRequested === false ? 1 : 0
        case 'createdAt':      return new Date(provider.createdAt).getTime()
        case 'stage':          return getOnboardingStage(provider)
        default:               return 0
    }
}

// ---------------------------------------------------------------------------
// Quick-filter definitions
// ---------------------------------------------------------------------------

const QUICK_FILTERS = [
    {
        key: 'notPublished',
        label: 'Not Published',
        test: p => p.profileStatus !== 'Published',
    },
    {
        key: 'verified',
        label: 'Verified',
        test: p => p.profileVerified === 'Verified',
    },
    {
        key: 'activeDelivery',
        label: 'Has Active Delivery',
        test: p => p.planDetails?.pauseRequested === false,
    },
    {
        key: 'missingDelivery',
        label: 'Missing Delivery Method',
        test: p => !p.deliveryDetails?.deliveryMethod,
    },
    {
        key: 'stuck',
        label: 'Stuck (>7 days, not active)',
        test: p => {
            if (p.planDetails?.pauseRequested === false) return false
            const daysDiff = (Date.now() - new Date(p.createdAt).getTime()) / (1000 * 60 * 60 * 24)
            return daysDiff > 7
        },
    },
    {
        key: 'adminNotAdded',
        label: 'Admin Not Added',
        test: p => {
            const pp = p.primaryPerson || {}
            return !pp.firstName || !pp.email
        },
    },
]

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

const COLUMNS = [
    { id: 'name',           label: 'Provider' },
    { id: 'profilePct',     label: 'Profile %' },
    { id: 'published',      label: 'Profile Status' },
    { id: 'adminAdded',     label: 'Admin Added' },
    { id: 'weeklyCap',      label: 'Weekly Cap' },
    { id: 'deliveryMethod', label: 'Delivery Method' },
    { id: 'activeDelivery', label: 'Delivery Status' },
    { id: 'smsStatus',      label: 'SMS Status' },
    { id: 'createdAt',      label: 'Date Created' },
]

// ---------------------------------------------------------------------------
// Small chip helper
// ---------------------------------------------------------------------------

function BoolChip({ yes, yesLabel = 'Yes', noLabel = 'No', classes }) {
    return yes
        ? <Chip label={yesLabel} className={classes.chipGreen} />
        : <Chip label={noLabel} className={classes.chipGrey} />
}

const useStyles = makeStyles(() => ({
    clickableRow: {
        cursor: 'pointer',
    },
    logo: {
        width: 36,
        height: 36,
        borderRadius: '4px',
        marginRight: '0.6rem',
        flexShrink: 0,
        '& img': {
            borderRadius: '4px !important',
        },
    },
    chipGreen: {
        backgroundColor: '#229626',
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
    },
    chipGrey: {
        backgroundColor: '#e9ecef',
        color: '#525f7f',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
    },
    chipYellow: {
        backgroundColor: '#fbc02d',
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
    },
    headerCell: {
        color: '#32325d',
        fontWeight: 700,
        whiteSpace: 'nowrap',
    },
    filterChip: {
        marginRight: 8,
        marginBottom: 4,
        cursor: 'pointer',
    },
}))

function Providers() {
    const classes = useStyles()
    const api = new AdminAPI()

    const [providers, setProviders] = useState([])
    const [brands, setBrands] = useState([])
    const [loading, setLoading] = useState(true)
    const [modalProvider, setModalProvider] = useState(null)
    const [productFilter, setProductFilter] = useState('Solar')
    const [quickFilter, setQuickFilter] = useState(null)
    const [sortColumn, setSortColumn] = useState('createdAt')
    const [sortDirection, setSortDirection] = useState('desc')
    const [addOpen, setAddOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        fetchProviders()
    }, [])

    async function fetchProviders() {
        try {
            const [providersResponse, brandsResponse] = await Promise.all([
                api.getProviders(),
                api.getBrands(),
            ])
            setProviders(providersResponse.filter(p => p.active))
            setBrands(brandsResponse.sort((a, b) => a.brandName.localeCompare(b.brandName)))
            setLoading(false)
        } catch (e) {
            onError(e)
        }
    }

    function handleSortClick(columnId) {
        if (sortColumn === columnId) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc')
        } else {
            setSortColumn(columnId)
            setSortDirection('asc')
        }
    }

    function toggleQuickFilter(key) {
        setQuickFilter(prev => prev === key ? null : key)
    }

    const filteredProviders = useMemo(() => {
        let list = providers

        if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase()
            list = list.filter(p => (p.name || '').toLowerCase().includes(q))
        }

        if (productFilter !== 'All') {
            list = list.filter(p => p.productsAvailable && p.productsAvailable[productFilter])
        }

        if (quickFilter) {
            const qf = QUICK_FILTERS.find(f => f.key === quickFilter)
            if (qf) list = list.filter(qf.test)
        }

        list = [...list].sort((a, b) => {
            const av = getSortValue(a, sortColumn)
            const bv = getSortValue(b, sortColumn)
            if (av < bv) return sortDirection === 'asc' ? -1 : 1
            if (av > bv) return sortDirection === 'asc' ? 1 : -1
            return 0
        })

        return list
    }, [providers, searchQuery, productFilter, quickFilter, sortColumn, sortDirection])

    function formatDate(iso) {
        if (!iso) return '—'
        return new Date(iso).toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })
    }

    return (
        <Container maxWidth={false} component={Box} marginTop='2rem'>
            {/* Toolbar */}
            <Box display='flex' justifyContent='space-between' alignItems='center' mb={1} flexWrap='wrap' style={{ gap: '0.5rem' }}>
                <Typography variant='h3'>Manage Providers</Typography>
                <Box display='flex' flexWrap='wrap' alignItems='center' style={{ gap: '0.75rem' }}>
                    <OutlinedInput
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        placeholder='Search providers...'
                        style={{ height: 40, minWidth: 220, backgroundColor: '#fff' }}
                        startAdornment={
                            <InputAdornment position='start'>
                                <SearchIcon style={{ color: '#8898aa', fontSize: '1.1rem' }} />
                            </InputAdornment>
                        }
                    />
                    <Button
                        variant='contained'
                        style={{ backgroundColor: '#229626', color: '#fff' }}
                        onClick={() => setAddOpen(true)}
                    >
                        Add Provider
                    </Button>
                    <Select
                        value={productFilter}
                        onChange={e => setProductFilter(e.target.value)}
                        variant='outlined'
                        style={{ minWidth: 160, height: 40 }}
                    >
                        <MenuItem value='All'>All Products</MenuItem>
                        {productTypes.map(pt => (
                            <MenuItem key={pt} value={pt}>{pt}</MenuItem>
                        ))}
                    </Select>

                </Box>
            </Box>

            {/* Quick-filter chips */}
            <Box mb={2} display='flex' flexWrap='wrap' alignItems='center'>
                <Typography variant='caption' style={{ color: '#8898aa', marginRight: 8 }}>
                    Quick filters:
                </Typography>
                {QUICK_FILTERS.map(qf => (
                    <Chip
                        key={qf.key}
                        label={qf.label}
                        size='small'
                        clickable
                        onClick={() => toggleQuickFilter(qf.key)}
                        className={classes.filterChip}
                        style={{
                            backgroundColor: quickFilter === qf.key ? '#32325d' : '#e9ecef',
                            color: quickFilter === qf.key ? '#fff' : '#525f7f',
                            fontWeight: quickFilter === qf.key ? 700 : 400,
                        }}
                    />
                ))}
            </Box>

            <AddProviderModal
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onAdded={(created) => {
                    setProviders(prev => [created, ...prev])
                    setModalProvider(created)
                }}
            />

            <ProviderModal
                open={!!modalProvider}
                onClose={() => setModalProvider(null)}
                provider={modalProvider}
                brands={brands}
                updateProviders={(updaterOrProviders) => {
                    setProviders(prev => {
                        const next = typeof updaterOrProviders === 'function'
                            ? updaterOrProviders(prev)
                            : updaterOrProviders
                        if (modalProvider) {
                            const updated = next.find(p => p.companyId === modalProvider.companyId)
                            if (updated) setModalProvider(updated)
                        }
                        return next
                    })
                }}
            />

            {loading ? (
                <Box display='flex' justifyContent='center' alignItems='center' height='30vh'>
                    <Loader />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                {COLUMNS.map(col => (
                                    <TableCell key={col.id} className={classes.headerCell}>
                                        <TableSortLabel
                                            active={sortColumn === col.id}
                                            direction={sortColumn === col.id ? sortDirection : 'asc'}
                                            onClick={() => handleSortClick(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredProviders.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={COLUMNS.length} align='center' style={{ color: '#8898aa', padding: '2rem' }}>
                                        No providers match the current filters.
                                    </TableCell>
                                </TableRow>
                            )}
                            {filteredProviders.map(provider => {
                                const { completed, total, pct } = getProfileProgress(provider)
                                const pp = provider.primaryPerson || {}
                                const adminAdded = !!(pp.firstName && pp.email)
                                const isPublished = provider.profileStatus === 'Published'
                                const weeklyCap = provider.planDetails?.maxLeads
                                const deliveryMethod = provider.deliveryDetails?.deliveryMethod
                                const isActive = provider.planDetails?.pauseRequested === false
                                const hasDelivery = !!deliveryMethod
                                const smsEnabled = provider.profileDetails?.smsFollowupsEnabled
                                const smsNotificationsEnabled = (provider.profileDetails?.smsNotificationNumbers || []).some(n => n.enabled)
                                return (
                                    <TableRow
                                        key={provider.companyId || provider.name}
                                        hover
                                        className={classes.clickableRow}
                                        onClick={() => setModalProvider(provider)}
                                    >
                                        {/* Provider */}
                                        <TableCell>
                                            <Box display='flex' alignItems='center'>
                                                <Avatar
                                                    variant='square'
                                                    src={provider.profileDetails?.logo}
                                                    alt={provider.name}
                                                    className={classes.logo}
                                                >
                                                    {provider.name?.charAt(0)}
                                                </Avatar>
                                                <Typography noWrap style={{ maxWidth: 200, fontSize: '0.875rem' }}>
                                                    {provider.name}
                                                </Typography>
                                            </Box>
                                        </TableCell>

                                        {/* Profile % */}
                                        <TableCell>
                                            <Tooltip title={`${completed} of ${total} sections complete`}>
                                                <Typography variant='body2' style={{ fontWeight: 600, color: pct === 100 ? '#229626' : '#32325d' }}>
                                                    {pct}%
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>

                                        {/* Published */}
                                        <TableCell>
                                            <BoolChip yes={isPublished} yesLabel='Published' noLabel='Not Published' classes={classes} />
                                        </TableCell>

                                        {/* Admin Added */}
                                        <TableCell>
                                            <BoolChip yes={adminAdded} yesLabel='Yes' noLabel='No' classes={classes} />
                                        </TableCell>

                                        {/* Weekly Cap */}
                                        <TableCell>
                                            <Typography variant='body2'>
                                                {weeklyCap || '—'}
                                            </Typography>
                                        </TableCell>

                                        {/* Delivery Method */}
                                        <TableCell>
                                            <Typography variant='body2'>
                                                {deliveryMethod || '—'}
                                            </Typography>
                                        </TableCell>

                                        {/* Delivery Status */}
                                        <TableCell>
                                            {!hasDelivery ? (
                                                <Chip label='Not set' className={classes.chipGrey} />
                                            ) : isActive ? (
                                                <Chip label='Active' className={classes.chipGreen} />
                                            ) : (
                                                <Chip label='Paused' className={classes.chipYellow} />
                                            )}
                                        </TableCell>

                                        {/* SMS Status */}
                                        <TableCell>
                                            {smsEnabled === undefined || smsEnabled === null ? (
                                                <Typography variant='body2'>—</Typography>
                                            ) : (
                                                <Chip
                                                    label={smsEnabled ? 'Active' : 'Inactive'}
                                                    className={smsEnabled ? classes.chipGreen : classes.chipGrey}
                                                />
                                            )}
                                        </TableCell>

                                        {/* Date Created */}
                                        <TableCell>
                                            <Typography variant='body2' style={{ whiteSpace: 'nowrap' }}>
                                                {formatDate(provider.createdAt)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Container>
    )
}

export default Providers